import {css} from 'aphrodite';
import React, {useMemo, useCallback} from 'react';
import {HandleType} from 'reactflow';

import getTestProps from 'test/lib/cypress/getTestProps';
import BenchlingIcon, {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';

import {
  NODE_HEADER_COLORS,
  NODE_HEADER_STYLES,
  NODE_STATUS_STYLES,
  noDragClass,
} from '../BenchlingNodeDesignConstants';
import {NodeStatus, NodeHandleProps, NodeIcon, NodeSection, NodeStatusStyles} from '../BenchlingNodeTypes';

export interface NodeStateStyleData {
  icon: BenchlingIconName;
  style: object;
}

// export for testing
export const NODE_STATE_DEFAULT_STYLES: Record<NodeStatus, NodeStateStyleData> = {
  ERROR: {
    icon: 'circle-xmark',
    style: NODE_STATUS_STYLES.errorIcon,
  },
  IN_PROGRESS: {
    icon: 'timer-outline',
    style: NODE_STATUS_STYLES.inProgressIcon,
  },
  PENDING: {
    icon: 'circle-outline',
    style: NODE_STATUS_STYLES.pendingIcon,
  },
  SUCCESS: {
    icon: 'check-circle',
    style: NODE_STATUS_STYLES.successIcon,
  },
  WARNING: {
    icon: 'exclamation-triangle',
    style: NODE_STATUS_STYLES.warningIcon,
  },
};

interface BenchlingNodeHeaderProps<TRowData> {
  id: string;
  data: {
    nodeName: string;
    sections: Array<NodeSection<TRowData>>;
    nodeIcon?: NodeIcon;
    headerIcon?: JSX.Element;
    nodeSecondaryInfo?: string;
    nodeHeaderHandleRenderer?: (props: NodeHandleProps) => JSX.Element | null;
    hideSectionSummaries?: boolean;
    headerColor?: string;
    onNodeHeaderClick?: (nodeId: string) => void;
  } & NodeStatusStyles;
}

function BenchlingNodeHeaderNodeIcon(props: {nodeIcon?: NodeIcon}): JSX.Element | null {
  if (!props.nodeIcon) {
    return null;
  }
  const baseProps = {
    className: css(NODE_HEADER_STYLES.nodeIcon),
    ...getTestProps({
      component: 'BenchlingNodeHeader',
      element: 'nodeIcon',
    }),
  };

  let icon;
  let style;
  if (typeof props.nodeIcon === 'string') {
    icon = props.nodeIcon;
    style = {};
  } else {
    icon = props.nodeIcon.icon;
    style = props.nodeIcon.color ? {color: props.nodeIcon.color} : {};
  }

  return <BenchlingIcon {...baseProps} icon={icon} style={style} />;
}

function BenchlingNodeHeaderNodeName(props: {nodeName: string}): JSX.Element {
  return (
    <div
      className={css(NODE_HEADER_STYLES.nodeName)}
      {...getTestProps({
        component: 'BenchlingNodeHeader',
        element: 'nodeName',
      })}
    >
      {props.nodeName}
    </div>
  );
}

function BenchlingNodeHeaderStatusIcon(props: {nodeStatus: NodeStatus}): JSX.Element {
  const nodeStatusIconData = NODE_STATE_DEFAULT_STYLES[props.nodeStatus];

  return (
    <BenchlingIcon
      className={css(NODE_HEADER_STYLES.statusIcon, nodeStatusIconData.style)}
      icon={nodeStatusIconData.icon}
      {...getTestProps({
        component: 'BenchlingNodeHeader',
        element: 'statusIcon',
      })}
    />
  );
}

/**
 * Renders a status icon for the Benchling node header. Used for the right-side icon in the header.
 *
 * If color is not provided and the icon is in the DEFAULT_NODE_STATUSES_TO_ICON_STYLE map, it will use the
 * corresponding style from the map. Otherwise, it will use the default status icon style.
 *
 * @argument nodeIcon - The icon to display in the header. Can be a just an icon name or an
 * object with an icon name and color.
 * @returns The status icon as a JSX element
 */
export function DefaultBenchlingNodeHeaderStatusIcon(props: {statusIcon: NodeIcon}): JSX.Element {
  const statusIcon = typeof props.statusIcon === 'string' ? props.statusIcon : props.statusIcon.icon;
  const style = typeof props.statusIcon === 'string' ? undefined : {color: props.statusIcon.color};
  return (
    <BenchlingIcon
      icon={statusIcon}
      className={css(NODE_HEADER_STYLES.statusIcon)}
      style={style}
      dataTestAttrs={{
        component: 'BenchlingNodeHeader',
        element: 'statusIcon',
      }}
    />
  );
}

function BenchlingNodeHeaderNodeSecondaryInfo(props: {nodeSecondaryInfo?: string}): JSX.Element | null {
  return props.nodeSecondaryInfo ? (
    <div
      className={css(NODE_HEADER_STYLES.secondaryInfo)}
      {...getTestProps({
        component: 'BenchlingNodeHeader',
        element: 'nodeSecondaryInfo',
      })}
    >
      {props.nodeSecondaryInfo}
    </div>
  ) : null;
}

function BenchlingNodeHeaderHandle(props: {
  nodeId: string;
  handleType: HandleType;
  nodeHeaderHandleRenderer?: (props: NodeHandleProps) => JSX.Element | null;
}): JSX.Element | null {
  if (!props.nodeHeaderHandleRenderer) {
    return null;
  }
  return (
    <div className={noDragClass()}>
      {props.nodeHeaderHandleRenderer?.({
        nodeId: props.nodeId,
        handleType: props.handleType,
      })}
    </div>
  );
}

/**
 * Header for the BenchlingNode.
 *
 * @argument nodeIcon - The left-side icon to display in the header. Can be a just an icon name or an
 * object with an icon name and color.
 *
 * @argument nodeStatus - The validation state of the node.
 * - Sets a preset border, header color, and status icon.
 * - If set to WARNING or ERROR, headerIcon and headerColor cannot be set.
 *
 * @argument headerIcon - The right-side icon to display in the header.
 * - Can be any JSX element. By default uses the DefaultBenchlingNodeHeaderStatusIcon component.
 * - Cannot be set if nodeStatus is WARNING or ERROR.
 * - If a custom component is used, consult the design team to ensure it meets benchling standards.
 *
 * @argument nodeSecondaryInfo - The secondary info to display below the node name.
 *
 * @argument headerColor - The background color of the header. Cannot be set if nodeStatus is
 * WARNING or ERROR.
 */
export function BenchlingNodeHeader<TRowData>(props: BenchlingNodeHeaderProps<TRowData>): JSX.Element {
  const handleNodeHeaderClick = useCallback(() => {
    if (props.data.onNodeHeaderClick) {
      props.data.onNodeHeaderClick(props.id);
    }
  }, [props.data, props.id]);

  const hasContentBelowHeader = useMemo(
    () => !props.data.hideSectionSummaries || props.data.sections.some(({hideSection}) => !hideSection),
    [props.data.hideSectionSummaries, props.data.sections]
  );

  return (
    <div
      className={css(
        NODE_HEADER_STYLES.nodeHeader,
        hasContentBelowHeader && NODE_HEADER_STYLES.nodeHeaderBorder,
        hasContentBelowHeader
          ? NODE_HEADER_STYLES.nodeHeaderWithContentBelow
          : NODE_HEADER_STYLES.nodeHeaderWithNoContentBelow
      )}
      style={
        props.data.nodeStatus
          ? {background: NODE_HEADER_COLORS[props.data.nodeStatus]}
          : props.data.headerColor
            ? {background: props.data.headerColor}
            : {}
      }
      {...getTestProps({
        component: 'BenchlingNodeHeader',
        element: 'root',
      })}
      onClick={handleNodeHeaderClick}
    >
      <div className={css(NODE_HEADER_STYLES.nodeHeaderContent)}>
        <div className={css(NODE_HEADER_STYLES.nodeHeaderTopRow)}>
          <div className={css(NODE_HEADER_STYLES.nodeHeaderTopRowLeft)}>
            {props.data.nodeIcon && (
              <div className={css(NODE_HEADER_STYLES.nodeHeaderTopRowIcon)}>
                <BenchlingNodeHeaderNodeIcon nodeIcon={props.data.nodeIcon} />
              </div>
            )}
            <BenchlingNodeHeaderNodeName nodeName={props.data.nodeName} />
          </div>
          <div className={css(NODE_HEADER_STYLES.nodeHeaderTopRowIcon)}>
            {props.data.nodeStatus ? (
              <BenchlingNodeHeaderStatusIcon nodeStatus={props.data.nodeStatus} />
            ) : (
              props.data.headerIcon && props.data.headerIcon
            )}
          </div>
        </div>
        <BenchlingNodeHeaderNodeSecondaryInfo nodeSecondaryInfo={props.data.nodeSecondaryInfo} />
      </div>
      <div>
        <BenchlingNodeHeaderHandle
          nodeId={props.id}
          handleType="target"
          nodeHeaderHandleRenderer={props.data.nodeHeaderHandleRenderer}
        />
        <BenchlingNodeHeaderHandle
          nodeId={props.id}
          handleType="source"
          nodeHeaderHandleRenderer={props.data.nodeHeaderHandleRenderer}
        />
      </div>
    </div>
  );
}
